import api from './api';
import type {
  UserProfile,
  ProfileUpdateRequest,
  PasswordChangeRequest,
  UserPreferences,
} from '@/types';

export const settingsService = {
  async getProfile(): Promise<UserProfile> {
    const response = await api.get<UserProfile>('/users/me/');
    return response.data;
  },

  async updateProfile(data: ProfileUpdateRequest): Promise<UserProfile> {
    const response = await api.patch<UserProfile>('/users/me/', data);
    return response.data;
  },

  async changePassword(data: PasswordChangeRequest): Promise<void> {
    // change_password is a detail=False action → /users/change_password/
    // (NOT nested under /users/me/, which only exists for `me` + `me/preferences/`).
    //
    // The form's field names differ from the serializer's, which is why this
    // used to fail with "New Password Confirm is required" (same root cause as
    // CUSTOMER-7 on the My Contacts card). Map at the boundary rather than
    // renaming the form fields.
    await api.post('/users/change_password/', {
      old_password: data.current_password,
      new_password: data.new_password,
      new_password_confirm: data.confirm_password,
    });
  },

  async getPreferences(): Promise<UserPreferences> {
    const response = await api.get<UserPreferences>('/users/me/preferences/');
    return response.data;
  },

  async updatePreferences(data: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await api.patch<UserPreferences>('/users/me/preferences/', data);
    return response.data;
  },

  async uploadProfilePicture(
    formData: FormData,
    onProgress?: (percent: number) => void,
  ): Promise<UserProfile> {
    // upload_picture is a detail=False action → /users/upload_picture/ (was
    // wrongly nested under /users/me/ → 404).
    //
    // 180s matches `assets.ts`, which overrides the same default for every
    // upload it owns. The 30s global in `api.ts` is what produced "timeout of
    // 30000ms exceeded" on a multi-megabyte photo; the server itself answers in
    // about 0.1s, so the ceiling was always the request body on a slow uplink.
    const response = await api.post<UserProfile>('/users/upload_picture/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
      onUploadProgress: (event) => {
        if (!onProgress) return;
        const total = event.total;
        if (!total) return;
        // Cap at 99: the last percent is the server finishing, and a bar that
        // sits at 100 for a beat reads as a hang.
        onProgress(Math.min(99, Math.round((event.loaded * 100) / total)));
      },
    });
    return response.data;
  },
};
